import {
  Alert,
  Box,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material"
import { FC } from "react"
import { ReservationSyncDto } from "src/features/things/reservationSyncApi"
import { fmtBytes, fmtNum } from "./ReservationSyncChrome"

const KIND_ORDER = ["drift", "sync", "revert", "snapshots"] as const

/** What this run actually stored, and where — the manifest, rendered. */
export const ObjectsPanel: FC<{ run: ReservationSyncDto }> = ({ run }) => {
  const byKind = KIND_ORDER.map((kind) => {
    const objects = run.objects.filter((o) => o.kind === kind)
    return {
      kind,
      objects,
      bytes: objects.reduce((a, o) => a + o.bytes, 0),
      rows: objects.reduce((a, o) => a + o.count, 0),
    }
  }).filter((g) => g.objects.length)

  return (
    <Box data-ui="UI-SL2W-166">
      <Alert severity="info" sx={{ mb: 2 }}>
        Report bodies never live in DynamoDB — this record keeps only the manifest below,
        and the bodies sit in S3. <strong>Full snapshots are their own objects</strong>,
        never loaded with a report: opening one row fetches only the part whose SST-id
        range covers it. Stored as plain JSON, uncompressed, so an audit object can be
        read with <code>aws s3 cp … | jq</code> without unpacking it.
      </Alert>

      <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
        {byKind.map((g) => (
          <Chip
            key={g.kind}
            size="small"
            variant="outlined"
            label={`${g.kind}: ${g.objects.length} object(s), ${fmtBytes(g.bytes)}, ${fmtNum(g.rows)} row(s)`}
          />
        ))}
        {run.snapshotsIncludeCodes && (
          <Chip
            size="small"
            color="error"
            variant="outlined"
            label="snapshots include access codes"
          />
        )}
      </Stack>

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Report</TableCell>
            <TableCell>Object key</TableCell>
            <TableCell align="right">Size</TableCell>
            <TableCell align="right">Rows</TableCell>
            <TableCell>SST id range</TableCell>
            <TableCell>Checksum</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {byKind.flatMap((g) =>
            g.objects.map((o) => (
              <TableRow key={o.key} hover>
                <TableCell>
                  <Chip size="small" variant="outlined" label={o.kind} />
                </TableCell>
                <TableCell sx={{ fontFamily: "monospace", fontSize: "0.72rem" }}>
                  {o.key}
                </TableCell>
                <TableCell align="right">{fmtBytes(o.bytes)}</TableCell>
                <TableCell align="right">
                  {fmtNum(o.count)}
                  <Typography variant="caption" sx={{ display: "block", color: "text.disabled" }}>
                    {fmtNum(o.rowFrom)}–{fmtNum(o.rowTo)}
                  </Typography>
                </TableCell>
                <TableCell sx={{ fontFamily: "monospace", fontSize: "0.68rem" }}>
                  {o.idFrom ? `${o.idFrom} … ${o.idTo}` : "—"}
                </TableCell>
                <TableCell sx={{ fontFamily: "monospace", fontSize: "0.68rem" }}>
                  {o.sha256.slice(0, 16)}…
                </TableCell>
              </TableRow>
            )),
          )}
        </TableBody>
      </Table>
    </Box>
  )
}
